(() => {
  if (window.__ssProductRowInitialized) return;
  window.__ssProductRowInitialized = true;

  const SELECTOR = '[id^="ss-product-recommendation-row-"]';
  const DESKTOP_MIN_WIDTH = 990;
  const sectionState = new WeakMap();

  function isAirconItem(item) {
    if (!item) return false;
    if (item.classList.contains('ss-product-recommendation-row__item--aircon')) return true;

    const type = (item.dataset.productType || '').toLowerCase();
    const tags = (item.dataset.productTags || '').toLowerCase();

    return (
      type.includes('aircon') ||
      type.includes('air conditioner') ||
      type.includes('split system') ||
      type.includes('wall split') ||
      tags.includes('aircon') ||
      tags.includes('air conditioner') ||
      tags.includes('split system')
    );
  }

  function shuffleArray(items) {
    const arr = [...items];
    for (let i = arr.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  function dedupeByHandle(items) {
    const seenHandles = new Set();

    return items.filter((item) => {
      const handle = item.dataset.productHandle || '';
      if (!handle) return true;
      if (seenHandles.has(handle)) return false;
      seenHandles.add(handle);
      return true;
    });
  }

  function getSectionItems(track) {
    return Array.from(track.querySelectorAll(':scope > .ss-product-recommendation-row__item'));
  }

  function getVisibleItems(track) {
    return getSectionItems(track).filter((item) => !item.hidden);
  }

  function getScrollAmount(track) {
    const firstVisibleItem = getVisibleItems(track)[0];
    if (!firstVisibleItem) return 260;

    const styles = window.getComputedStyle(track);
    const gap = parseInt(styles.columnGap || styles.gap || '0', 10);

    return Math.ceil(firstVisibleItem.offsetWidth + gap);
  }

  function updateButtons(section) {
    const state = sectionState.get(section);
    if (!state) return;

    const { track, prevBtn, nextBtn } = state;
    if (!track || !prevBtn || !nextBtn) return;

    const visibleItems = getVisibleItems(track);

    if (!visibleItems.length) {
      prevBtn.hidden = true;
      nextBtn.hidden = true;
      return;
    }

    const maxScrollLeft = Math.max(track.scrollWidth - track.clientWidth, 0);
    const isScrollable = track.scrollWidth > track.clientWidth + 8;

    prevBtn.disabled = track.scrollLeft <= 4;
    nextBtn.disabled = track.scrollLeft >= maxScrollLeft - 4;

    const shouldHide = !isScrollable && window.innerWidth >= DESKTOP_MIN_WIDTH;
    prevBtn.hidden = shouldHide;
    nextBtn.hidden = shouldHide;
  }

  function scheduleUpdateButtons(section) {
    const state = sectionState.get(section);
    if (!state) return;

    if (state.updateButtonsRaf) cancelAnimationFrame(state.updateButtonsRaf);

    state.updateButtonsRaf = requestAnimationFrame(() => {
      state.updateButtonsRaf = null;
      updateButtons(section);
    });
  }

  function applyRecommendationRules(section) {
    const state = sectionState.get(section);
    if (!state) return;

    const { track, prevBtn, nextBtn, emptyState, isProductPage, sourceIsAircon, productsToShow } = state;
    const allItems = getSectionItems(track);

    if (!allItems.length) {
      if (emptyState) emptyState.hidden = false;
      if (prevBtn) prevBtn.hidden = true;
      if (nextBtn) nextBtn.hidden = true;
      return;
    }

    let manualItems = [];
    let autoItems = [];

    for (const item of allItems) {
      if (item.hasAttribute('data-auto-candidate')) {
        autoItems.push(item);
      } else {
        manualItems.push(item);
      }
    }

    if (isProductPage && sourceIsAircon) {
      manualItems = manualItems.filter((item) => !isAirconItem(item));
      autoItems = autoItems.filter((item) => !isAirconItem(item));
    }

    manualItems = dedupeByHandle(manualItems);
    autoItems = dedupeByHandle(autoItems);
    autoItems = shuffleArray(autoItems);

    const finalItems = [...manualItems, ...autoItems].slice(0, productsToShow);
    const finalSet = new Set(finalItems);

    for (const item of allItems) {
      item.hidden = !finalSet.has(item);
    }

    for (const item of finalItems) {
      track.appendChild(item);
    }

    if (emptyState) {
      emptyState.hidden = finalItems.length > 0;
    }

    scheduleUpdateButtons(section);
  }

  function bindCarousel(section) {
    const state = sectionState.get(section);
    if (!state || state.bound) return;

    const { track, prevBtn, nextBtn } = state;

    state.handlePrevClick = () => {
      track.scrollBy({
        left: -getScrollAmount(track),
        behavior: 'smooth',
      });
    };

    state.handleNextClick = () => {
      track.scrollBy({
        left: getScrollAmount(track),
        behavior: 'smooth',
      });
    };

    state.handleScroll = () => {
      scheduleUpdateButtons(section);
    };

    state.handleResize = () => {
      scheduleUpdateButtons(section);
    };

    if (prevBtn) {
      prevBtn.addEventListener('click', state.handlePrevClick);
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', state.handleNextClick);
    }

    track.addEventListener('scroll', state.handleScroll, { passive: true });
    window.addEventListener('resize', state.handleResize, { passive: true });

    state.bound = true;
  }

  function initRecommendationSection(section) {
    if (!section || section.dataset.ssRecoInit === 'true') return;

    const track = section.querySelector('[data-carousel-track]');
    const prevBtn = section.querySelector('.ss-product-recommendation-row__arrow--prev');
    const nextBtn = section.querySelector('.ss-product-recommendation-row__arrow--next');
    const emptyState = section.querySelector('[data-empty-state]');

    if (!track) return;

    sectionState.set(section, {
      track,
      prevBtn,
      nextBtn,
      emptyState,
      isProductPage: section.dataset.isProductPage === 'true',
      sourceIsAircon: section.dataset.sourceIsAircon === 'true',
      productsToShow: parseInt(section.dataset.productsToShow || '8', 10),
      bound: false,
      updateButtonsRaf: null,
      handlePrevClick: null,
      handleNextClick: null,
      handleScroll: null,
      handleResize: null,
    });

    bindCarousel(section);
    applyRecommendationRules(section);
    scheduleUpdateButtons(section);

    section.dataset.ssRecoInit = 'true';
  }

  function observeSections(scope) {
    const root = scope || document;
    const sections = root.querySelectorAll(SELECTOR);

    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries, obs) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          initRecommendationSection(entry.target);
          obs.unobserve(entry.target);
        }
      },
      {
        rootMargin: '250px 0px',
      }
    );

    sections.forEach((section) => {
      if (section.dataset.ssRecoInit !== 'true') {
        observer.observe(section);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        observeSections(document);
      },
      { once: true }
    );
  } else {
    observeSections(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    observeSections(event.target);
  });

  document.addEventListener('shopify:block:select', (event) => {
    observeSections(event.target);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    observeSections(event.target);
  });
})();